/**
 * <pdp-overlays> — every overlay on the PDP v2 page.
 *
 * IMPORTANT: triggers are wired by DELEGATION FROM document, never bound to the trigger node.
 * assets/product-info.js:157 updateOptionValues() hands the freshly fetched <variant-selects> to
 * HTMLUpdateUtility.viewTransition, which REPLACES the whole subtree. Any listener attached to a
 * ⓘ button dies the first time a shopper picks a variant — silently, with correct-looking markup.
 * See ruling R1 in docs/superpowers/plans/2026-08-24-pdp-overlays.md.
 *
 * Every overlay is a native <dialog> opened with showModal(), which supplies the focus trap, Esc,
 * focus restore and top-layer rendering. The four presentations are pure CSS.
 */
const SCROLL_LOCK = 'pdp-v2-scroll-lock';
const SCROLL_PAD = '--pdp-scrollbar-w';

/**
 * Lock the page behind an open overlay WITHOUT letting it jump sideways.
 *
 * `html.pdp-v2-scroll-lock { overflow: hidden }` is what stops the page scrolling under a modal
 * (showModal() traps focus but does not stop scrolling). Its side effect is that the scrollbar
 * disappears, the content box gets that much wider, and everything on the page slides -- centred
 * content by half the scrollbar width -- at the moment the overlay opens.
 *
 * So measure the scrollbar and hand it to the stylesheet, which puts the width back as padding.
 * The measurement MUST happen before the class lands: once `overflow: hidden` applies the
 * scrollbar is already gone and this reads 0.
 *
 * On a platform with overlay scrollbars (macOS by default, and headless Chromium) the gap is 0,
 * nothing is published, and the rule resolves to its `0px` fallback -- correct, because there is
 * no jump to correct there.
 */
const lockScroll = () => {
  const gap = window.innerWidth - document.documentElement.clientWidth;
  if (gap > 0) document.documentElement.style.setProperty(SCROLL_PAD, `${gap}px`);
  document.documentElement.classList.add(SCROLL_LOCK);
};

/** Both halves come off together -- a stranded padding is a permanent gutter down the page. */
const unlockScroll = () => {
  document.documentElement.classList.remove(SCROLL_LOCK);
  document.documentElement.style.removeProperty(SCROLL_PAD);
};

class PdpOverlays extends HTMLElement {
  connectedCallback() {
    this.dialogs = new Map();
    this.querySelectorAll('dialog[data-pdp-overlay-name]').forEach((d) => {
      this.dialogs.set(d.dataset.pdpOverlayName, d);
      // Native light-dismiss: a click that lands on the <dialog> box itself (not on the panel
      // inside it) is a backdrop click.
      // A backdrop click closes -- including one that lands on the trigger's own box, which is
      // how a second tap on the ⓘ toggles its popover shut.
      d.addEventListener('click', (e) => { if (e.target === d) this.close(); });
      // 'cancel' (Esc) fires BEFORE the UA removes the `open` attribute; 'close' fires in a later,
      // separate queued task, after `open` is already gone. Reacting only to 'close' leaves a real
      // window where `open` has been removed but the scroll lock has not — cancel closes it in the
      // same task as the attribute removal, and 'close' still covers close() called directly (e.g.
      // the backdrop-click path above, which never fires 'cancel').
      d.addEventListener('cancel', () => this.onClose(d));
      d.addEventListener('close', () => this.onClose(d));
      this.wireDrag(d);

      // Visually-hidden close button (ruling M22): the same close() path as backdrop-click below,
      // not a second close mechanism. It exists only to give showModal()'s focus trap something to
      // hold — see the comment above each button in pdp-value-panel.liquid / pdp-info-overlay.liquid.
      const closeBtn = d.querySelector('[data-pdp-overlay-close]');
      if (closeBtn) closeBtn.addEventListener('click', () => this.close());
    });

    this.onDocClick = (e) => {
      const trigger = e.target.closest('[data-pdp-overlay]');
      if (!trigger) return;
      const name = trigger.dataset.pdpOverlay;
      if (!this.dialogs.has(name)) return;
      e.preventDefault();
      this.open(name, trigger.dataset.pdpItem, trigger);
    };
    document.addEventListener('click', this.onDocClick);

    // Hover-to-open was tried on 2026-09-10 and reverted the same day at the user's request: the
    // ⓘ opens and closes on tap/click only. (A modal popover that opened on hover also blocked
    // every pointer interaction beneath it until the pointer left.)
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocClick);

    // Remove the lock unconditionally, NOT via onClose(): onClose() early-returns whenever any
    // dialog still reports `open`, and a dialog being torn down along with this host element
    // still reports `open` right up until it's gone. Routing through onClose() here would strand
    // `overflow: hidden` on <html> forever on a section unload with an overlay open (review
    // finding 1c), freezing scroll on the whole page, not just this section.
    unlockScroll();
  }

  open(name, item, trigger = null) {
    const dialog = this.dialogs.get(name);
    if (!dialog) return;
    this.trigger = trigger;

    dialog.querySelectorAll('[data-item]').forEach((el) => {
      if (el.dataset.item === item) el.setAttribute('data-raised', 'true');
      else el.removeAttribute('data-raised');
    });

    // Clear any inline left/top left behind by a PREVIOUS desktop anchor before this dialog shows
    // again, unless it's about to be re-anchored anyway. This has to happen here, at the start of
    // open(), not in onClose(): on the Esc path 'cancel' fires BEFORE the UA removes `open` (see
    // the comment in onClose() below), so clearing there strips the anchor coordinates out from
    // under a dialog that is still open and mid-exit-fade, teleporting it to its static position
    // for the rest of the transition. Doing it here instead only ever touches a dialog that is
    // either about to be freshly positioned, or is a sheet — where a stale inline left/top would
    // otherwise override the sheet's own `inset: auto 0 0 0`.
    const willAnchor = dialog.classList.contains('pdp-v2-dialog--info') &&
      window.matchMedia('(min-width: 990px)').matches && trigger;
    if (!willAnchor) {
      dialog.style.left = '';
      dialog.style.top = '';
    }

    dialog.showModal();
    lockScroll();
    dialog.dispatchEvent(new Event('pdp:open'));
    if (willAnchor) {
      this.anchor(dialog, trigger);
    }
    // The trigger reads as "open" (inverted) for as long as its overlay shows -- see the CSS note
    // on .pdp-v2__info-trigger.is-open.
    if (trigger) trigger.classList.add('is-open');
  }

  close() {
    this.dialogs.forEach((d) => { if (d.open) d.close(); });
  }

  onClose(closing = null) {
    // Exclude the dialog that is in the middle of closing: on the 'cancel' path its `open`
    // attribute has not been removed yet, so checking it directly would wrongly look like "still
    // open" and skip cleanup.
    if ([...this.dialogs.values()].some((d) => d !== closing && d.open)) return;
    unlockScroll();
    if (this.trigger) this.trigger.classList.remove('is-open');
    document.querySelectorAll('.pdp-v2__info-trigger.is-open').forEach((el) => el.classList.remove('is-open'));
    // <dialog> restores focus itself in current browsers; this is the belt-and-braces path for the
    // case where the trigger was re-rendered (variant change) while the overlay was open.
    if (this.trigger?.isConnected) this.trigger.focus();
    this.trigger = null;
  }

  /**
   * Position the desktop popover beside its trigger.
   *
   * The dialog paints in the top layer, so its coordinates are viewport coordinates — exactly what
   * getBoundingClientRect() returns. Measure AFTER showModal(), or offsetHeight is 0.
   */
  anchor(dialog, trigger) {
    const TAIL = 27.5; // how far the tail tip protrudes past the card (the mockup's triangle, user 2026-10-22)
    // The tail tip sits this far from the trigger: 20 from an option's ⓘ (user, 2026-09-10: "about
    // 20px"); 0 from an at-a-glance feature's cell, 20px closer (user, 2026-10-22), since the cell is
    // already wider than its icon.
    const GAP = dialog.classList.contains('pdp-v2-dialog--feature') ? 0 : 20;
    const EDGE = 16;   // minimum clearance from the viewport
    const card = dialog.querySelector('.pdp-v2-popover');
    const t = trigger.getBoundingClientRect();
    const w = card.offsetWidth;
    const h = card.offsetHeight;

    let left = t.right + GAP + TAIL;
    let flip = false;
    if (left + w > window.innerWidth - EDGE) {
      left = t.left - GAP - TAIL - w;
      flip = true;
    }
    left = Math.max(EDGE, Math.min(left, window.innerWidth - w - EDGE));

    const top = Math.max(EDGE, Math.min(
      t.top + t.height / 2 - h / 2,
      window.innerHeight - h - EDGE
    ));

    dialog.dataset.flip = String(flip);
    dialog.style.left = `${left}px`;
    dialog.style.top = `${top}px`;
  }

  /**
   * Drag-to-dismiss for the bottom sheets. Pointer Events only — one code path covers touch, pen
   * and mouse, which is also what makes it testable with Playwright's mouse API.
   */
  /*
   * Swipe to close (designer, Pastel #29 / #31: "drawer doesn't allow move gesture to close", "the
   * open door should also close with move/swipe"). The sheet (below 990) drags DOWN, the value drawer
   * (990+) drags RIGHT, and the panel follows the pointer; letting go past a quarter of its size, or
   * on a flick, closes it, anything less springs back. Until 2026-10-08 only the sheet's 2px handle
   * started a drag. Now the whole panel does, except:
   *   - a press on a link, button or field (they keep their click);
   *   - a gesture that is mostly ACROSS the closing axis, or against it (that is a scroll);
   *   - on touch, when the sheet's content overflows: touch-action then lets it scroll and only the
   *     handle drags (a mouse or pen drags from anywhere).
   * On the desktop drawer a horizontal two-finger trackpad swipe drives it the same way: macOS reports
   * fingers moving right as a NEGATIVE deltaX under natural scrolling, so the drawer follows -deltaX
   * and settles 140ms after the last wheel event. The ⓘ popover (990+) is anchored and does not drag.
   */
  wireDrag(dialog) {
    const panel = dialog.querySelector('.pdp-v2-panel, .pdp-v2-sheet');
    const handle = dialog.querySelector('.pdp-v2-panel__handle, .pdp-v2-sheet__handle');
    if (!panel) return;

    const desktopMq = window.matchMedia('(min-width: 990px)');
    const isDrawer = panel.classList.contains('pdp-v2-panel');
    // 'x' for the desktop drawer, 'y' for any sheet, null where nothing drags (the desktop popover).
    const axis = () => (desktopMq.matches ? (isDrawer ? 'x' : null) : 'y');
    let g = null;   // the live gesture
    let wheelTimer = 0;

    const set = (d) => { panel.style.translate = g.ax === 'x' ? `${d}px 0` : `0 ${d}px`; };
    // Idempotent: safe from the gesture's own end, from the dialog's 'cancel' / 'close' and from a
    // breakpoint crossing mid-gesture.
    const teardown = () => {
      if (!g) return;
      if (g.pointerId != null) panel.releasePointerCapture?.(g.pointerId);
      clearTimeout(wheelTimer);
      g = null;
      panel.removeAttribute('data-dragging');
      panel.style.translate = '';
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', end);
      document.removeEventListener('pointercancel', end);
    };
    // data-dragging (transition: none) and the inline translate are dropped in the SAME task as
    // `open` is removed (or not), so the CSS transition runs from where the finger let go -- out to
    // closed, or back to 0.
    const settle = () => {
      if (!g) return;
      const size = g.ax === 'x' ? panel.offsetWidth : panel.offsetHeight;
      const shut = g.d > size * 0.25 || (g.v > 0.6 && g.d > 24);
      teardown();
      if (shut) this.close();
    };

    const move = (e) => {
      if (!g || e.pointerId !== g.pointerId) return;
      if (axis() !== g.ax) { teardown(); return; }   // a breakpoint crossed mid-gesture
      const along = g.ax === 'x' ? e.clientX - g.x : e.clientY - g.y;
      const across = g.ax === 'x' ? e.clientY - g.y : e.clientX - g.x;
      if (!g.live) {
        if (Math.abs(along) < 6 && Math.abs(across) < 6) return;
        if (Math.abs(across) > Math.abs(along) || along < 0) { teardown(); return; }
        g.live = true;
        panel.setAttribute('data-dragging', 'true');
        panel.setPointerCapture?.(e.pointerId);
      }
      const now = performance.now();
      const d = Math.max(0, along);
      g.v = (d - g.d) / Math.max(1, now - g.t);
      g.d = d;
      g.t = now;
      set(d);
      e.preventDefault();
    };
    const end = (e) => {
      if (!g || (e && e.pointerId !== g.pointerId)) return;
      if (!g.live) { teardown(); return; }
      settle();
    };

    panel.addEventListener('pointerdown', (e) => {
      const ax = axis();
      if (!ax || !dialog.open || g || e.button !== 0) return;
      const onHandle = !!handle && handle.contains(e.target);
      if (!onHandle && e.target.closest('a, button, input, select, textarea, label, summary')) return;
      if (ax === 'y' && e.pointerType === 'touch') return;   // touch on a sheet: the touch path below
      if (ax === 'y' && !onHandle && panel.scrollTop > 0) return;   // mid-scroll: pulling down scrolls back up
      g = { ax, x: e.clientX, y: e.clientY, d: 0, v: 0, t: performance.now(), pointerId: e.pointerId, live: false };
      document.addEventListener('pointermove', move);
      document.addEventListener('pointerup', end);
      document.addEventListener('pointercancel', end);
    });

    /*
     * Touch on a sheet (user, 2026-10-09: "the price match modal does not close properly"). On a short
     * phone the sheet hits its 85vh cap and its content scrolls, and leaving vertical touch to the
     * browser meant only the 2px handle could close it. The bottom-sheet rule instead: a gesture that
     * starts with the content at the top and moves DOWN drags the sheet (its touchmove is
     * preventDefault-ed from the first move, before any scroll begins); anything else scrolls.
     */
    let tg = null;
    panel.addEventListener('touchstart', (e) => {
      if (axis() !== 'y' || !dialog.open || g || e.touches.length !== 1) { tg = null; return; }
      const onHandle = !!handle && handle.contains(e.target);
      if (!onHandle && e.target.closest('a, button, input, select, textarea, label, summary')) { tg = null; return; }
      const t = e.touches[0];
      tg = { x: t.clientX, y: t.clientY, decided: false };
    }, { passive: true });
    panel.addEventListener('touchmove', (e) => {
      if (!tg) return;
      const t = e.touches[0];
      const dx = t.clientX - tg.x;
      const dy = t.clientY - tg.y;
      if (!tg.decided) {
        if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
        tg.decided = true;
        if (!(dy > 0 && Math.abs(dy) > Math.abs(dx) && panel.scrollTop <= 0)) { tg = null; return; }
        g = { ax: 'y', x: tg.x, y: tg.y, d: 0, v: 0, t: performance.now(), pointerId: null, live: true, touch: true };
        panel.setAttribute('data-dragging', 'true');
      }
      if (!g || !g.touch) return;
      e.preventDefault();
      const now = performance.now();
      const d = Math.max(0, dy);
      g.v = (d - g.d) / Math.max(1, now - g.t);
      g.d = d;
      g.t = now;
      set(d);
    }, { passive: false });
    const touchEnd = () => {
      tg = null;
      if (g && g.touch) settle();
    };
    panel.addEventListener('touchend', touchEnd);
    panel.addEventListener('touchcancel', touchEnd);

    panel.addEventListener('wheel', (e) => {
      if (axis() !== 'x' || !dialog.open) return;
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      if (g && !g.wheel) return;
      if (!g) {
        g = { ax: 'x', d: 0, v: 0, t: performance.now(), pointerId: null, live: true, wheel: true };
        panel.setAttribute('data-dragging', 'true');
      }
      const now = performance.now();
      const d = Math.max(0, g.d - e.deltaX);
      g.v = (d - g.d) / Math.max(1, now - g.t);
      g.d = d;
      g.t = now;
      set(d);
      e.preventDefault();
      clearTimeout(wheelTimer);
      wheelTimer = setTimeout(settle, 140);
    }, { passive: false });

    // touch-action, re-read on every open: a sheet whose content fits takes vertical touch drags
    // itself; one that overflows leaves vertical panning to the browser. The drawer always lets
    // vertical panning through and takes the horizontal.
    dialog.addEventListener('pdp:open', () => {
      if (axis() === 'x') panel.style.touchAction = 'pan-y';
      else panel.style.touchAction = panel.scrollHeight > panel.clientHeight + 1 ? 'pan-y' : 'none';
    });

    // Cancel any in-progress drag when the dialog closes by ANY path -- Escape ('cancel'), backdrop
    // click or programmatic close() ('close'). Without this the panel freezes at its mid-drag
    // position for the whole exit transition: `[data-dragging='true'] { transition: none; }` sets
    // transition-property to none, and the exit rule only overrides transition-duration. (Review
    // finding 1.) And react to the breakpoint itself, not only to move/end (review finding 2).
    dialog.addEventListener('cancel', teardown);
    dialog.addEventListener('close', teardown);
    desktopMq.addEventListener('change', teardown);
  }
}

if (!customElements.get('pdp-overlays')) customElements.define('pdp-overlays', PdpOverlays);
